import React, { useContext, useState, KeyboardEvent } from 'react'
import * as yup from 'yup'
import styled from 'styled-components'
import { space, SpaceProps } from 'styled-system'
import { GridItem } from '@zuto/core/src/grid'
import { Email } from '@zuto/icons'
import { Input } from '@zuto/atoms'
import { useCallbackOnce } from '@zuto/utilities'
import QuestionComponentProps from '../../../Questions/question-component-props'
import EmailAddressValidator from '../../../validators/EmailAddressValidator'
import QuestionKey from '../../../Questions/question-key'
import { ApplicationFormContext } from '../../ApplicationForm/ApplicationFormContext'
import AppFormQuestion from '../../AppFormQuestion'
import withQuestion from '../../HOC/withQuestion'
import withFade from '../../HOC/withFade'
import { fireReactGAEvent, handlePrevButtonClick } from '../utils'
import NavigationButtons from '../../NavigationButtons/NavigationButtons'

const StyledDiv = styled.div<SpaceProps>`
  ${space}
  `

const EmailAddressQuestion = ({
  existingAnswers,
  disablePrevious
}: QuestionComponentProps): JSX.Element => {
  const appFormContext = useContext(ApplicationFormContext)
  const [inputError, setInputError] = useState<string | undefined>(undefined)
  const [emailAddress, setEmailAddress] = useState(existingAnswers)
  const [nextButtonCb, resetNextQuestionCb] = useCallbackOnce()
  const [previousButtonCb] = useCallbackOnce()

  const schema = EmailAddressValidator().getValidator()

  const valdiateInput = (): boolean => {
    try {
      schema.validateSync({
        emailAddress
      })
    } catch (e: unknown) {
      const err = e as yup.ValidationError
      fireReactGAEvent({
        category: 'Application Form',
        action: 'Validation Error',
        label: 'Email Address',
        nonInteraction: true
      })
      setInputError(err.errors[0])
      return false
    }
    setInputError(undefined)
    return true
  }

  const nextQuestion = () => {
    const valid = valdiateInput()
    if (!valid) {
      resetNextQuestionCb()
      return
    }
    appFormContext.dispatch({
      type: 'QUESTION_ANSWERED',
      name: QuestionKey.EMAIL_ADDRESS,
      value: emailAddress
    })
  }

  const previousQuestion = () => {
    handlePrevButtonClick(
      appFormContext.dispatch,
      { name: QuestionKey.EMAIL_ADDRESS, value: emailAddress }
    )
  }

  const inputOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailAddress(e.currentTarget.value)
  }

  const handleEnterKeyPress = (e: KeyboardEvent) => {
    if (e.code === 'Enter') {
      nextButtonCb(nextQuestion)
    }
  }

  return (
    <>
      <GridItem gridColumn={['auto / span 12', '3 / span 8']} minHeight="174px">
        <AppFormQuestion
          questionKey={QuestionKey.EMAIL_ADDRESS}
          mt={3}
          mb={2}
          textStyle="material.body.medium.smallLineHeight"
        >
          What&apos;s  your email address?
        </AppFormQuestion>
        <StyledDiv mb={5}>
          <Input
            defaultValue={emailAddress}
            onChange={inputOnChange}
            onKeyPress={handleEnterKeyPress}
            name="email-address"
            label="Email address"
            labelVariant="experimental"
            error={inputError}
            icon={<Email />}
            data-testid="email-input"
            type="email"
            maxLength={100}
            data-hj-suppress=""
          />
        </StyledDiv>
      </GridItem>
      <NavigationButtons
        nextHandler={() => nextButtonCb(nextQuestion)}
        previousHandler={() => previousButtonCb(previousQuestion)}
        disableNext={!emailAddress}
        disablePrevious={disablePrevious ? 'invisible' : undefined}
      />
    </>
  )
}

export default withFade(withQuestion(EmailAddressQuestion))
